import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Empty, Badge, Modal } from "@/components/ui-kit";
import { fmtDateTime } from "@/lib/utils";
import { Mail, Send, Trash2, Pencil, Cake, PartyPopper, RefreshCw, MessageCircle, Bell, UserPlus, Handshake } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  welcome: { label: "Bienvenue", color: "primary", Icon: PartyPopper },
  birthday: { label: "Anniversaire", color: "yellow", Icon: Cake },
  convoc_reminder: { label: "Rappel convocation", color: "blue", Icon: Bell },
  affiliation_request: { label: "Dossier enfant", color: "yellow", Icon: UserPlus },
  trial_affiliation: { label: "Proposition d'affiliation", color: "green", Icon: Handshake },
};

export default function Emails() {
  const [tab, setTab] = useState("draft");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [dEmail, setDEmail] = useState(true);
  const [dBubble, setDBubble] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const load = () => {
    setLoading(true);
    setSelected(new Set());
    api.get(`/emails/outbox?status=${tab}`).then((r) => setItems(r.data)).catch(() => toast.error("Erreur de chargement")).finally(() => setLoading(false));
  };
  useEffect(load, [tab]);

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(items.map((m) => m.id)));
  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Supprimer ${selected.size} email(s) sélectionné(s) ?`)) return;
    try {
      const { data } = await api.post("/emails/outbox/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${data.deleted} email(s) supprimé(s)`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const openEdit = (m) => { setEditing(m); setSubject(m.subject || ""); setBody(m.body || ""); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/emails/outbox/${editing.id}`, { subject, body });
      toast.success("Brouillon mis à jour");
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const openSend = (m) => {
    const hasEmail = (m.to || []).length > 0;
    setSendTarget(m);
    setDEmail(hasEmail);
    setDBubble(hasEmail ? false : !!m.has_linked_account);
  };

  const doSend = async () => {
    const m = sendTarget;
    if (!dEmail && !dBubble) { toast.error("Choisissez au moins un mode d'envoi."); return; }
    setSendingId(m.id);
    try {
      const { data } = await api.post(`/emails/outbox/${m.id}/send`, { send_email: dEmail, send_bubble: dBubble });
      const parts = [];
      if (data.email?.length) parts.push(`email (${data.email.join(", ")})`);
      if (data.bubble?.length) parts.push(`bulle (${data.bubble.join(", ")})`);
      toast.success(parts.length ? `Envoyé — ${parts.join(" · ")}` : "Envoyé");
      setSendTarget(null); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible"); }
    finally { setSendingId(null); }
  };

  const remove = async (m) => {
    if (!window.confirm("Supprimer ce brouillon d'email ?")) return;
    try { await api.delete(`/emails/outbox/${m.id}`); toast.success("Brouillon supprimé"); load(); }
    catch { toast.error("Erreur"); }
  };

  const genBirthdays = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/emails/generate-birthdays");
      toast.success(data.created ? `${data.created} email(s) d'anniversaire préparé(s)` : "Aucun anniversaire aujourd'hui");
      if (tab === "draft") load();
    } catch { toast.error("Erreur"); }
    finally { setGenLoading(false); }
  };

  const genConvoc = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/emails/generate-convocation-reminders");
      toast.success(data.created ? `${data.created} rappel(s) de convocation préparé(s)` : "Aucun rappel à générer (aucune séance demain avec des convoqués sans réponse)");
      if (tab === "draft") load();
    } catch { toast.error("Erreur"); }
    finally { setGenLoading(false); }
  };

  const genAffiliation = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/emails/generate-affiliation-requests");
      toast.success(data.created ? `${data.created} demande(s) de dossier enfant préparée(s)` : "Aucun parent sans enfant rattaché");
      if (tab === "draft") load();
    } catch { toast.error("Erreur"); }
    finally { setGenLoading(false); }
  };

  const genTrialAffiliation = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/emails/generate-trial-affiliations");
      toast.success(data.created ? `${data.created} proposition(s) d'affiliation préparée(s)` : "Aucun essai n'a encore atteint 2 séances");
      if (tab === "draft") load();
    } catch { toast.error("Erreur"); }
    finally { setGenLoading(false); }
  };

  return (
    <div data-testid="emails-page">
      <PageHeader title="Emails à envoyer" subtitle="Relisez et modifiez chaque email avant de l'envoyer aux familles."
        action={(
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="generate-trial-affiliations-btn" variant="outline" onClick={genTrialAffiliation} disabled={genLoading}>
              <Handshake size={16} /> {genLoading ? "..." : "Proposer l'affiliation (2 séances d'essai)"}
            </Button>
            <Button data-testid="generate-affiliation-requests-btn" variant="outline" onClick={genAffiliation} disabled={genLoading}>
              <UserPlus size={16} /> {genLoading ? "..." : "Demander les dossiers enfants"}
            </Button>
            <Button data-testid="generate-convoc-reminders-btn" variant="outline" onClick={genConvoc} disabled={genLoading}>
              <Bell size={16} /> {genLoading ? "..." : "Générer les rappels de convocation"}
            </Button>
            <Button data-testid="generate-birthdays-btn" variant="outline" onClick={genBirthdays} disabled={genLoading}>
              <RefreshCw size={16} /> {genLoading ? "..." : "Générer les anniversaires du jour"}
            </Button>
          </div>
        )} />

      <div className="flex gap-2 mb-5">
        {[{ k: "draft", l: "À valider" }, { k: "sent", l: "Envoyés" }].map((t) => (
          <button key={t.k} data-testid={`emails-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${tab === t.k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {loading ? <Empty text="Chargement..." /> : items.length ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap px-1" data-testid="emails-selection-bar">
            <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
              <input type="checkbox" data-testid="select-all-emails" checked={allSelected} onChange={toggleSelectAll}
                className="w-4 h-4 accent-[color:var(--primary)]" />
              Tout sélectionner
            </label>
            {selected.size > 0 && (
              <>
                <span className="text-xs text-muted-foreground" data-testid="emails-selected-count">{selected.size} sélectionné(s)</span>
                <Button data-testid="bulk-delete-emails-btn" variant="outline" onClick={bulkDelete}
                  className="!text-destructive !border-destructive/40 hover:!bg-destructive/10">
                  <Trash2 size={15} /> Supprimer la sélection
                </Button>
              </>
            )}
          </div>
          {items.map((m) => {
            const meta = TYPE_META[m.type] || { label: m.type, color: "gray", Icon: Mail };
            const Icon = meta.Icon;
            const checked = selected.has(m.id);
            return (
              <Card key={m.id} data-testid="email-row" className={`py-4 ${checked ? "ring-2 ring-primary" : ""}`}>
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <input type="checkbox" data-testid={`select-email-${m.id}`} checked={checked} onChange={() => toggleSel(m.id)}
                      className="w-4 h-4 mt-1 shrink-0 accent-[color:var(--primary)]" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge color={meta.color}><span className="flex items-center gap-1"><Icon size={12} /> {meta.label}</span></Badge>
                        <span className="font-bold text-sm truncate">{m.subject}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        À : {m.recipient_label} · {(m.to || []).join(", ") || "aucun destinataire"}
                      </p>
                      <div className="mt-3 rounded-lg border border-border bg-secondary/20 p-3 max-h-40 overflow-hidden text-sm text-muted-foreground"
                        dangerouslySetInnerHTML={{ __html: m.body }} />
                      {m.sent_at && <p className="text-xs text-primary mt-2">Envoyé le {fmtDateTime(m.sent_at)}</p>}
                    </div>
                  </div>
                  {m.status === "draft" && (
                    <div className="flex sm:flex-col gap-2 shrink-0">
                      <Button data-testid={`edit-email-btn-${m.id}`} variant="outline" onClick={() => openEdit(m)}>
                        <Pencil size={15} /> Lire / Modifier
                      </Button>
                      <Button data-testid={`send-email-btn-${m.id}`} onClick={() => openSend(m)} disabled={sendingId === m.id || (!(m.to || []).length && !m.has_linked_account)}>
                        <Send size={15} /> {sendingId === m.id ? "Envoi..." : "Envoyer"}
                      </Button>
                      <button data-testid={`delete-email-btn-${m.id}`} onClick={() => remove(m)}
                        className="p-2 text-muted-foreground hover:text-destructive transition-colors self-center" title="Supprimer">
                        <Trash2 size={17} />
                      </button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      ) : <Empty text={tab === "draft" ? "Aucun email en attente de validation." : "Aucun email envoyé."} />}

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Relire & modifier l'email">
        {editing && (
          <div>
            <p className="text-xs text-muted-foreground mb-3">Destinataires : <b className="text-foreground">{(editing.to || []).join(", ")}</b></p>
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Objet</label>
            <input data-testid="email-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary mb-4" />
            <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Message (HTML autorisé)</label>
            <textarea data-testid="email-body-input" rows={9} value={body} onChange={(e) => setBody(e.target.value)}
              className="w-full rounded-lg bg-secondary border border-border p-3 text-sm outline-none focus:border-primary resize-y font-mono" />
            <div className="mt-3">
              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Aperçu</p>
              <div className="rounded-lg border border-border bg-secondary/20 p-3 text-sm text-muted-foreground"
                dangerouslySetInnerHTML={{ __html: body }} />
            </div>
            <div className="flex gap-2 mt-4">
              <Button data-testid="save-email-btn" onClick={saveEdit} disabled={saving} className="flex-1">
                {saving ? "Enregistrement..." : "Enregistrer"}
              </Button>
              <Button variant="outline" onClick={() => { saveEdit().then(() => openSend(editing)); }} disabled={saving}>
                <Send size={15} /> Enregistrer & envoyer
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!sendTarget} onClose={() => setSendTarget(null)} title="Envoyer le message">
        {sendTarget && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-bold">{sendTarget.subject}</p>
              <p className="text-xs text-muted-foreground mt-1">Destinataire : {sendTarget.recipient_label}</p>
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Modes d'envoi</p>
            <label data-testid="deliver-email-toggle" className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${dEmail ? "border-primary bg-primary/10" : "border-border bg-secondary/40"} ${!(sendTarget.to || []).length ? "opacity-50 cursor-not-allowed" : ""}`}>
              <input type="checkbox" className="w-4 h-4 mt-0.5 accent-[color:var(--primary)]" checked={dEmail} disabled={!(sendTarget.to || []).length}
                onChange={(e) => setDEmail(e.target.checked)} />
              <span className="text-sm">
                <span className="flex items-center gap-1.5 font-semibold"><Mail size={14} /> Par email</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{(sendTarget.to || []).length ? (sendTarget.to || []).join(", ") : "Aucune adresse email disponible"}</span>
              </span>
            </label>
            <label data-testid="deliver-bubble-toggle" className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${dBubble ? "border-primary bg-primary/10" : "border-border bg-secondary/40"} ${!sendTarget.has_linked_account ? "opacity-50 cursor-not-allowed" : ""}`}>
              <input type="checkbox" className="w-4 h-4 mt-0.5 accent-[color:var(--primary)]" checked={dBubble} disabled={!sendTarget.has_linked_account}
                onChange={(e) => setDBubble(e.target.checked)} />
              <span className="text-sm">
                <span className="flex items-center gap-1.5 font-semibold"><MessageCircle size={14} /> Dans la bulle de conversation</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{sendTarget.has_linked_account ? "Le message arrive dans le chat interne (canal Club / Admin) du destinataire." : "Aucun compte lié à ce destinataire."}</span>
              </span>
            </label>
            <Button data-testid="confirm-send-btn" className="w-full" onClick={doSend} disabled={sendingId === sendTarget.id || (!dEmail && !dBubble)}>
              <Send size={15} /> {sendingId === sendTarget.id ? "Envoi..." : "Envoyer maintenant"}
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
